// @spec GHG-STR-030
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateEmissionTarget } from '@/features/ghg/api/use-emission-targets';
import { EmissionTargetForm } from '@/features/ghg/components/EmissionTargetForm';
import type { EmissionTargetFormValues } from '@/features/ghg/types';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/targets/new')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.newEmissionTarget'),
  }),
  component: NewEmissionTargetPage,
});

/**
 * Create new emission target page.
 *
 * Provides a form to configure a new emission reduction target,
 * including baseline data, reduction percentages, scope coverage,
 * SBTi alignment, and interim milestones.
 */
function NewEmissionTargetPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const createTarget = useCreateEmissionTarget();

  const handleBack = () => {
    void navigate({ to: '/ghg/targets', search: { section: 'targets' } });
  };

  const handleSubmit = (data: EmissionTargetFormValues) => {
    createTarget.mutate(
      {
        name: data.name,
        targetType: data.targetType,
        baseYear: data.baseYear,
        baseYearEmissions: parseFloat(data.baseYearEmissions),
        targetYear: data.targetYear,
        targetReductionPercent: parseFloat(data.targetReductionPercent),
        scopeCoverage: data.scopeCoverage,
        scienceBased: data.scienceBased,
        sbtiClassification: data.sbtiClassification || undefined,
        milestones: data.milestones,
        notes: data.notes || undefined,
      },
      {
        onSuccess: (result) => {
          toast.success(t('toast.emissionTargetCreatedSuccessfully'));
          void navigate({ to: '/ghg/targets/$targetId', params: { targetId: result.id } });
        },
        onError: () => {
          toast.error(t('toast.failedToCreateEmissionTarget'));
        },
      }
    );
  };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Header */}
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('targetNew.back')}
          </Button>
          <h1 className="text-2xl font-semibold">{t('targetNew.heading')}</h1>
          <p className="text-muted-foreground">
            {t('targetNew.description')}
          </p>
        </div>

        {/* Form Card */}
        <Card>
          <CardHeader>
            <CardTitle>{t('targetNew.cardTitle')}</CardTitle>
            <CardDescription>
              {t('targetNew.cardDescription')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EmissionTargetForm
              onSubmit={handleSubmit}
              onCancel={handleBack}
              isSubmitting={createTarget.isPending}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
